import React from 'react';
import {
  Inbox, UserX, UserCheck, Circle, Loader, Clock, Hourglass,
  ShieldCheck, CheckCircle, CheckCheck, Archive, Flag,
  PanelLeftClose, PanelLeftOpen, ChevronDown, Plus, Trash2, Edit2, Check, X, ArrowUp, ArrowDown, Bookmark,
} from 'lucide-react';
import { confirmDialog } from '../../lib/confirmStore';

export type TicketSection =
  | 'all'
  | 'unassigned'
  | 'me'
  | 'open'
  | 'in_progress'
  | 'pending'
  | 'waiting_applicant'
  | 'waiting_staff'
  | 'resolved'
  | 'closed'
  | 'archived'
  | 'high_priority';

export const TICKET_SECTIONS: { id: TicketSection; label: string; icon: React.ElementType }[] = [
  { id: 'all', label: 'All Tickets', icon: Inbox },
  { id: 'unassigned', label: 'Unassigned', icon: UserX },
  { id: 'me', label: 'Assigned to Me', icon: UserCheck },
  { id: 'open', label: 'Open', icon: Circle },
  { id: 'in_progress', label: 'In Progress', icon: Loader },
  { id: 'pending', label: 'Pending', icon: Clock },
  { id: 'waiting_applicant', label: 'Waiting on Applicant', icon: Hourglass },
  { id: 'waiting_staff', label: 'Waiting on Staff', icon: ShieldCheck },
  { id: 'resolved', label: 'Resolved', icon: CheckCircle },
  { id: 'closed', label: 'Closed', icon: CheckCheck },
  { id: 'archived', label: 'Archived', icon: Archive },
  { id: 'high_priority', label: 'High Priority', icon: Flag },
];

export const TICKET_SECTION_LABELS: Record<TicketSection, string> = TICKET_SECTIONS.reduce(
  (acc, s) => { acc[s.id] = s.label; return acc; },
  {} as Record<TicketSection, string>,
);

export interface CustomView {
  id: string;
  label: string;
  section: TicketSection;
}

const CUSTOM_VIEWS_KEY = 'isats:custom-ticket-views:v1';

const DEFAULT_CUSTOM_VIEWS: CustomView[] = [
  { id: 'view_unassigned_urgent', label: 'Unassigned High Priority', section: 'unassigned' },
  { id: 'view_waiting_staff', label: 'Action Needed (Staff)', section: 'waiting_staff' },
];

function loadSavedCustomViews(): CustomView[] {
  if (typeof window === 'undefined') return DEFAULT_CUSTOM_VIEWS;
  try {
    const raw = window.localStorage.getItem(CUSTOM_VIEWS_KEY);
    if (!raw) return DEFAULT_CUSTOM_VIEWS;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_CUSTOM_VIEWS;
  } catch {
    return DEFAULT_CUSTOM_VIEWS;
  }
}

function saveCustomViews(views: CustomView[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CUSTOM_VIEWS_KEY, JSON.stringify(views));
  } catch { /* best-effort */ }
}

interface TicketSidebarProps {
  section: TicketSection;
  onSectionChange: (s: TicketSection) => void;
  counts: Record<TicketSection, number>;
  unreadCounts: Record<TicketSection, number>;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

/**
 * Left navigation sidebar for the Admin support-ticket workspace — ticket
 * sections with live counts and unread badges, collapsible to icons on desktop.
 */
export default function TicketSidebar({
  section, onSectionChange, counts, unreadCounts, collapsed, onToggleCollapse,
}: TicketSidebarProps) {
  const [customViews, setCustomViews] = React.useState<CustomView[]>(loadSavedCustomViews);
  const [isAddingView, setIsAddingView] = React.useState(false);
  const [newViewName, setNewViewName] = React.useState('');
  const [editingViewId, setEditingViewId] = React.useState<string | null>(null);
  const [editingViewName, setEditingViewName] = React.useState('');

  const handleSaveView = () => {
    if (!newViewName.trim()) return;
    const newView: CustomView = {
      id: `view_${Date.now()}`,
      label: newViewName.trim(),
      section,
    };
    const updated = [...customViews, newView];
    setCustomViews(updated);
    saveCustomViews(updated);
    setNewViewName('');
    setIsAddingView(false);
  };

  const handleRenameView = (id: string) => {
    if (!editingViewName.trim()) return;
    const updated = customViews.map(v => v.id === id ? { ...v, label: editingViewName.trim() } : v);
    setCustomViews(updated);
    saveCustomViews(updated);
    setEditingViewId(null);
    setEditingViewName('');
  };

  const handleDeleteView = async (id: string) => {
    const view = customViews.find(v => v.id === id);
    if (!(await confirmDialog(`Delete the saved view "${view?.label || 'this view'}"? This cannot be undone.`, { confirmLabel: 'Delete' }))) return;
    const updated = customViews.filter(v => v.id !== id);
    setCustomViews(updated);
    saveCustomViews(updated);
  };

  const handleMoveView = (index: number, direction: 'up' | 'down') => {
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= customViews.length) return;
    const updated = [...customViews];
    const [moved] = updated.splice(index, 1);
    updated.splice(targetIdx, 0, moved);
    setCustomViews(updated);
    saveCustomViews(updated);
  };

  return (
    <div
      className={`shrink-0 hidden lg:flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden transition-[width] duration-200 ${
        collapsed ? 'w-12' : 'w-60'
      }`}
    >
      <div className="flex items-center justify-between px-2.5 py-2.5 border-b border-slate-200 bg-slate-50/80 shrink-0">
        {!collapsed && <span className="text-xs font-bold text-slate-700 truncate">Ticket Views</span>}
        <button
          onClick={onToggleCollapse}
          className="p-1 rounded-lg hover:bg-slate-100 transition-all cursor-pointer shrink-0"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          type="button"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen className="h-3.5 w-3.5 text-slate-400" /> : <PanelLeftClose className="h-3.5 w-3.5 text-slate-400" />}
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto p-1.5 space-y-0.5" aria-label="Ticket views">
        {TICKET_SECTIONS.map(({ id, label, icon: Icon }) => {
          const isActive = section === id;
          const count = counts[id] ?? 0;
          const unread = unreadCounts[id] ?? 0;
          return (
            <button
              key={id}
              onClick={() => onSectionChange(id)}
              className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-[10px] transition-all cursor-pointer outline-none focus:ring-2 focus:ring-inset focus:ring-brand-primary ${
                isActive ? 'bg-blue-50 text-brand ring-1 ring-inset ring-blue-200' : 'text-slate-600 hover:bg-slate-50'
              }`}
              title={collapsed ? label : undefined}
              type="button"
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {!collapsed && <span className="text-[11px] font-semibold truncate flex-1 text-left">{label}</span>}
              {!collapsed && (
                <span className="flex items-center gap-1 shrink-0">
                  {unread > 0 && (
                    <span className="min-w-4 px-1 h-4 bg-blue-100 text-brand text-[11px] font-extrabold rounded-full flex items-center justify-center">
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                  <span className={`text-[11px] font-bold tabular-nums ${isActive ? 'text-brand/70' : 'text-slate-400'}`}>{count}</span>
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Saved Views Section */}
      {!collapsed && (
        <div className="border-t border-slate-200 p-1.5 space-y-1">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 flex items-center gap-1">
              <Bookmark className="h-3 w-3" /> Saved Views
            </span>
            <button
              onClick={() => setIsAddingView(a => !a)}
              className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all cursor-pointer"
              title="Save current view"
              type="button"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>

          {isAddingView && (
            <div className="p-1.5 bg-slate-50 rounded-lg border border-slate-200 space-y-1.5">
              <input
                type="text"
                value={newViewName}
                onChange={e => setNewViewName(e.target.value)}
                placeholder="Custom view name..."
                className="w-full px-2 py-1 text-xs border border-slate-200 rounded outline-none bg-white"
                autoFocus
              />
              <div className="flex items-center justify-end gap-1">
                <button
                  onClick={() => setIsAddingView(false)}
                  className="px-2 py-0.5 text-[10px] font-bold text-slate-500 hover:bg-slate-200 rounded cursor-pointer"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveView}
                  disabled={!newViewName.trim()}
                  className="px-2 py-0.5 text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded cursor-pointer"
                  type="button"
                >
                  Save
                </button>
              </div>
            </div>
          )}

          <div className="space-y-0.5 max-h-36 overflow-y-auto">
            {customViews.map((view, index) => {
              const isActive = section === view.section;
              const isEditing = editingViewId === view.id;
              return (
                <div key={view.id} className="group flex items-center justify-between gap-1 px-2 py-1 rounded-lg hover:bg-slate-50 text-slate-600">
                  {isEditing ? (
                    <div className="flex items-center gap-1 flex-1">
                      <input
                        type="text"
                        value={editingViewName}
                        onChange={e => setEditingViewName(e.target.value)}
                        className="w-full px-1.5 py-0.5 text-[11px] border border-blue-300 rounded outline-none"
                        autoFocus
                      />
                      <button aria-label="Save view name" title="Save view name" onClick={() => handleRenameView(view.id)} className="p-0.5 text-emerald-600 hover:bg-emerald-50 rounded"><Check className="h-3 w-3" /></button>
                      <button aria-label="Cancel rename" title="Cancel rename" onClick={() => setEditingViewId(null)} className="p-0.5 text-slate-400 hover:bg-slate-100 rounded"><X className="h-3 w-3" /></button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => onSectionChange(view.section)}
                        className={`flex-1 text-left text-[11px] font-semibold truncate cursor-pointer ${isActive ? 'text-brand font-bold' : 'text-slate-600 hover:text-slate-900'}`}
                        type="button"
                      >
                        {view.label}
                      </button>
                      <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                        {index > 0 && (
                          <button onClick={() => handleMoveView(index, 'up')} className="p-0.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded" title="Move up"><ArrowUp className="h-2.5 w-2.5" /></button>
                        )}
                        {index < customViews.length - 1 && (
                          <button onClick={() => handleMoveView(index, 'down')} className="p-0.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded" title="Move down"><ArrowDown className="h-2.5 w-2.5" /></button>
                        )}
                        <button
                          onClick={() => { setEditingViewId(view.id); setEditingViewName(view.label); }}
                          className="p-0.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
                          title="Rename view"
                        >
                          <Edit2 className="h-2.5 w-2.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteView(view.id)}
                          className="p-0.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                          title="Delete view"
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!collapsed && (
        <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/50 shrink-0">
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Sections combine with the search and filter bar above the list.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Horizontal section strip for small screens (mirrors the desktop sidebar).
 */
export function TicketSectionStrip({
  section, onSectionChange, counts, unreadCounts,
}: {
  section: TicketSection;
  onSectionChange: (s: TicketSection) => void;
  counts: Record<TicketSection, number>;
  unreadCounts: Record<TicketSection, number>;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const visible = expanded ? TICKET_SECTIONS : TICKET_SECTIONS.slice(0, 4);
  return (
    <div className="lg:hidden bg-white border border-slate-200 rounded-xl p-1.5">
      <div className="flex items-center gap-1 overflow-x-auto">
        {visible.map(({ id, label, icon: Icon }) => {
          const isActive = section === id;
          const unread = unreadCounts[id] ?? 0;
          return (
            <button
              key={id}
              onClick={() => { onSectionChange(id); setExpanded(false); }}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                isActive ? 'bg-blue-50 text-brand ring-1 ring-inset ring-blue-200' : 'text-slate-600 hover:bg-slate-50'
              }`}
              type="button"
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="text-[10px] font-bold">{label}</span>
              {unread > 0 && <span className="min-w-4 px-1 h-4 bg-brand-primary text-white text-[11px] font-extrabold rounded-full flex items-center justify-center">{unread}</span>}
            </button>
          );
        })}
        <button
          onClick={() => setExpanded(e => !e)}
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-slate-500 hover:bg-slate-50 cursor-pointer shrink-0"
          type="button"
          aria-label={expanded ? 'Show fewer sections' : 'Show more sections'}
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
    </div>
  );
}
